import { type CSSProperties, useEffect, useMemo, useState } from 'react';

import { cn } from '../../utils/cn';
import {
  loaderNow,
  logoLoaderContinuesWait,
  markLogoLoaderHandoff,
  trackLogoLoader,
} from './logo-loader.handoff';
import { cubicBezierAt, LOGO_LOADER_DELAY_MS } from './logo-loader.motion';
import {
  LOGO_LOADER_BOOT_ID,
  LOGO_LOADER_CLASS,
  LOGO_LOADER_CSS,
  LOGO_LOADER_FLIGHT_TARGET_ATTR,
  logoLoaderMarkSvg,
  logoLoaderPhaseVars,
} from './logo-loader-styles';
import type { LogoLoaderProps } from './LogoLoader.types';

/** The index.html boot loader, if it is on screen right now. */
function bootLoaderShowing(now: number): boolean {
  if (typeof document === 'undefined') return false;
  const el = document.getElementById(LOGO_LOADER_BOOT_ID);
  if (!el) return false;
  const since = Number(el.dataset.t ?? 0);
  return now - since >= LOGO_LOADER_DELAY_MS;
}

const NON_VISUAL = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'LINK', 'META']);

/** How the boot mark bows out. */
const BOOT_EXIT = {
  /** Longest wait for the page's own logo to be laid out before giving up on the flight. */
  targetWaitMs: 500,
  /** Cards ease from mid-shuffle back to the plain mark. */
  settleMs: 240,
  /** The mark travels into the page's logo. */
  flyMs: 560,
  flyEase: [0.65, 0, 0.35, 1] as const,
  /** The dotted ground lifts off the page underneath. */
  groundOutMs: 320,
  groundDelayMs: 80,
  /** Mark → the page's own logo cross-fade on arrival; also the plain exit. */
  fadeMs: 180,
} as const;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );
}

/** The page's own logo to fly into: the first one laid out on screen. */
function findFlightTarget(): HTMLElement | null {
  const nodes = document.querySelectorAll<HTMLElement>(
    `[${LOGO_LOADER_FLIGHT_TARGET_ATTR}]`,
  );
  for (const node of Array.from(nodes)) {
    const r = node.getBoundingClientRect();
    if (r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight)
      return node;
  }
  return null;
}

/**
 * Freeze every shuffling part of the mark where it is, then ease it to rest.
 * The mark's own fade-in is left running: freezing it mid-way would fly a
 * half-transparent mark.
 */
function settleMark(boot: HTMLElement): void {
  for (const node of Array.from(
    boot.querySelectorAll<SVGElement>(`.${LOGO_LOADER_CLASS.anim}`),
  )) {
    for (const anim of node.getAnimations()) {
      try {
        anim.commitStyles();
      } catch {
        // A part not rendered yet has nothing to commit.
      }
      anim.cancel();
    }
    node.animate([{ transform: 'none' }], {
      duration: BOOT_EXIT.settleMs,
      easing: 'ease-out',
      fill: 'forwards',
    });
  }
}

function fadeOutAndRemove(boot: HTMLElement): void {
  const done = () => boot.remove();
  if (typeof boot.animate !== 'function') return done();
  boot
    .animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: BOOT_EXIT.fadeMs,
      easing: 'ease-out',
      fill: 'forwards',
    })
    .finished.then(done, done);
}

/** The boot mark travels into `target` (the page's own logo) while the ground lifts. */
function flyInto(boot: HTMLElement, target: HTMLElement): void {
  const markBox = boot.querySelector<HTMLElement>(`.${LOGO_LOADER_CLASS.root}`);
  const svg = boot.querySelector<SVGElement>(`.${LOGO_LOADER_CLASS.mark}`);
  const ground = boot.querySelector<HTMLElement>(`.${LOGO_LOADER_CLASS.ground}`);
  if (!markBox || !svg) return fadeOutAndRemove(boot);
  settleMark(boot);
  const from = svg.getBoundingClientRect();
  const fromX = from.left + from.width / 2;
  const fromY = from.top + from.height / 2;
  // The page's logo waits for the mark to land on it.
  const previousOpacity = target.style.opacity;
  const previousTransition = target.style.transition;
  target.style.opacity = '0';
  ground?.animate([{ opacity: 1 }, { opacity: 0 }], {
    duration: BOOT_EXIT.groundOutMs,
    delay: BOOT_EXIT.groundDelayMs,
    easing: 'ease-out',
    fill: 'forwards',
  });
  const land = () => {
    target.style.transition = `opacity ${BOOT_EXIT.fadeMs}ms ease-out`;
    target.style.opacity = previousOpacity;
    const restore = () => {
      target.style.transition = previousTransition;
      boot.remove();
    };
    markBox
      .animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: BOOT_EXIT.fadeMs,
        easing: 'ease-out',
        fill: 'forwards',
      })
      .finished.then(restore, restore);
  };
  // Driven per frame, re-measuring the logo each time: the page's logo may
  // still be easing into place (its own entrance), and the mark must land
  // where the logo IS, not where it was when the flight began.
  const startedAt = loaderNow();
  const step = () => {
    const p = Math.min(1, (loaderNow() - startedAt) / BOOT_EXIT.flyMs);
    const e = cubicBezierAt(BOOT_EXIT.flyEase, p);
    const to = target.getBoundingClientRect();
    const dx = (to.left + to.width / 2 - fromX) * e;
    const dy = (to.top + to.height / 2 - fromY) * e;
    const scale = 1 + (to.height / Math.max(1, from.height) - 1) * e;
    markBox.style.transform = `translate(${dx}px, ${dy}px) scale(${scale})`;
    if (p < 1) requestAnimationFrame(step);
    else land();
  };
  requestAnimationFrame(step);
}

/**
 * Retire the index.html boot loader once the app has drawn something real into
 * `root` — and leave a hand-off note, so an app loader that takes over carries
 * the shuffle on without a blink. An app that renders nothing yet (a redirect
 * gate) keeps the boot loader up, which is the point.
 *
 * `flyToLogo` (the landing): if the mark was on screen, it settles and FLIES
 * into the page's own logo (`data-fl-logo-target`) while the dotted ground
 * lifts — the loader becomes the nav's mark instead of vanishing. Otherwise
 * (or with Reduce Motion) it fades out.
 */
export function releaseBootLogoLoader(
  root: HTMLElement,
  { flyToLogo = false }: { flyToLogo?: boolean } = {},
): void {
  if (typeof document === 'undefined') return;
  // Scripts / styles React hoists into the root draw nothing; neither does the
  // crawler copy (hidden for JS clients).
  const hasContent = () =>
    Array.from(root.children).some(
      (child) => child.id !== 'seo-static' && !NON_VISUAL.has(child.tagName),
    );
  const release = () => {
    const boot = document.getElementById(LOGO_LOADER_BOOT_ID);
    if (!boot) return;
    if (!bootLoaderShowing(loaderNow())) {
      boot.remove();
      return;
    }
    markLogoLoaderHandoff();
    if (!flyToLogo || prefersReducedMotion() || typeof boot.animate !== 'function') {
      fadeOutAndRemove(boot);
      return;
    }
    // The page's logo may lay out a frame or two after its first commit.
    const startedAt = loaderNow();
    const seek = () => {
      const target = findFlightTarget();
      if (target) return flyInto(boot, target);
      if (loaderNow() - startedAt > BOOT_EXIT.targetWaitMs) return fadeOutAndRemove(boot);
      requestAnimationFrame(seek);
    };
    seek();
  };
  if (hasContent()) {
    release();
    return;
  }
  const observer = new MutationObserver(() => {
    if (!hasContent()) return;
    observer.disconnect();
    release();
  });
  observer.observe(root, { childList: true });
}

/**
 * Is a page-load wait already on screen (an app loader, or the index.html boot
 * loader past its grace)? A loader about to mount uses it to take the SAME
 * place as the one it continues — see the app's PageLoader.
 */
export function logoLoaderWaitInProgress(): boolean {
  const now = loaderNow();
  return logoLoaderContinuesWait(now) || bootLoaderShowing(now);
}

/**
 * Web: the page-load loader — the Flashka mark shuffling its two cards. Inline
 * SVG + CSS keyframes, no animation runtime, so it moves on its very first
 * paint. The stylesheet is a React 19 hoisted `<style>` (deduped by `href`), so
 * any number of loaders share one copy; index.html carries the same CSS for the
 * pre-React boot loader. Theme comes from the `.dark` class on <html>.
 *
 * Loaders that follow one another in a single wait hand over seamlessly: same
 * page clock, and no second grace period (see `logo-loader.handoff`).
 */
export function LogoLoader({
  size = 48,
  label,
  accessibilityLabel,
  fill = false,
  immediate = false,
  className,
  testID,
}: LogoLoaderProps) {
  // Decided once, at first render, so the loader never flips mid-wait.
  const [start] = useState(() => {
    const now = loaderNow();
    return {
      atOnce: immediate || logoLoaderContinuesWait(now) || bootLoaderShowing(now),
      phase: logoLoaderPhaseVars(now) as CSSProperties,
    };
  });
  useEffect(() => trackLogoLoader(start.atOnce), [start.atOnce]);

  const mark = useMemo(() => ({ __html: logoLoaderMarkSvg(size) }), [size]);
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={label ? undefined : accessibilityLabel}
      data-immediate={start.atOnce ? '' : undefined}
      data-testid={testID}
      style={start.phase}
      className={cn(LOGO_LOADER_CLASS.root, fill && LOGO_LOADER_CLASS.fill, className)}>
      <style href={LOGO_LOADER_CLASS.root} precedence="default">
        {LOGO_LOADER_CSS}
      </style>
      <span dangerouslySetInnerHTML={mark} style={{ display: 'contents' }} />
      {label ? (
        <span
          className={cn(
            LOGO_LOADER_CLASS.label,
            'text-sm font-medium text-secondary-500',
          )}>
          {label}
          <span className={LOGO_LOADER_CLASS.dots} aria-hidden="true">
            <i>.</i>
            <i>.</i>
            <i>.</i>
          </span>
        </span>
      ) : null}
    </div>
  );
}
